import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NodeSelection } from '@tiptap/pm/state';
import { useFloating, offset, flip, shift } from '@floating-ui/react';
import { Button } from '@/components/tiptap-ui-primitive/button';
import { Separator } from '@/components/tiptap-ui-primitive/separator';
import { TrashIcon } from '@/components/tiptap-icons/trash-icon';
import { PaintBucketIcon } from '@/components/tiptap-icons/paint-bucket-icon';
import { PlusIcon } from '@/components/tiptap-icons/plus-icon';
import {
  POST_EDITOR_FLOATING_MENU_KINDS,
  POST_EDITOR_BUBBLE_MENU_OFFSET,
  POST_EDITOR_BUBBLE_MENU_PLACEMENT,
  pathstoneQuoteHasAttribution,
  postEditorBlockquoteFloatingReference,
  postEditorBubbleMenuFloatingStyles,
  postEditorFloatingContainerClasses,
  postEditorFloatingPositionStyle,
  postEditorFloatingToolbarGroupClasses,
  postEditorFloatingToolbarProps,
} from '../lib/post_editor_constants.js';
import { QuoteColorPickerMenu } from './QuoteColorPickerMenu.jsx';

function isPathstoneQuoteDom(dom) {
  return dom instanceof HTMLElement && dom.classList.contains('ps-pathstone-quote');
}

/** Resolve the active Pathstone blockquote node, doc position, and DOM element. */
export function findActivePathstoneBlockquote(editor) {
  if (!editor?.state) return null;

  const { selection } = editor.state;

  if (selection instanceof NodeSelection) {
    const { node, from } = selection;
    if (node?.type?.name === 'blockquote') {
      const dom = editor.view.nodeDOM(from);
      if (isPathstoneQuoteDom(dom)) {
        return { node, pos: from, dom };
      }
    }
  }

  const { $from } = selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type.name !== 'blockquote') continue;
    const node = $from.node(depth);
    const pos = $from.before(depth);
    const dom = editor.view.nodeDOM(pos);
    if (isPathstoneQuoteDom(dom)) {
      return { node, pos, dom };
    }
  }

  return null;
}

export function BlockquoteNodeFloating({ editor }) {
  const floatingRef = useRef(null);
  const colorAnchorRef = useRef(null);
  const [active, setActive] = useState(null);
  const [colorOpen, setColorOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const refreshActive = useCallback(() => {
    if (!editor) {
      setActive(null);
      return;
    }
    setActive(findActivePathstoneBlockquote(editor));
  }, [editor]);

  useEffect(() => {
    if (!editor) return undefined;
    refreshActive();
    editor.on('selectionUpdate', refreshActive);
    editor.on('transaction', refreshActive);
    return () => {
      editor.off('selectionUpdate', refreshActive);
      editor.off('transaction', refreshActive);
    };
  }, [editor, refreshActive]);

  useEffect(() => {
    const checkModal = () => {
      setIsModalOpen(!!document.querySelector('.resource-modal-overlay'));
    };
    checkModal();
    const observer = new MutationObserver(checkModal);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!colorOpen) return undefined;
    const close = (e) => {
      if (colorAnchorRef.current?.contains(e.target)) return;
      setColorOpen(false);
    };
    document.addEventListener('mousedown', close, true);
    return () => document.removeEventListener('mousedown', close, true);
  }, [colorOpen]);

  const referenceElement = active?.dom
    ? postEditorBlockquoteFloatingReference(active.dom)
    : null;
  const visible = !!referenceElement && !isModalOpen;

  const { refs, floatingStyles, update } = useFloating({
    elements: { reference: referenceElement, floating: floatingRef.current },
    placement: POST_EDITOR_BUBBLE_MENU_PLACEMENT,
    middleware: [
      offset(POST_EDITOR_BUBBLE_MENU_OFFSET),
      flip({ mainAxis: false, crossAxis: true }),
      shift({ padding: 5 }),
    ],
    strategy: 'absolute',
  });

  const adjustedFloatingStyles = useMemo(
    () => postEditorBubbleMenuFloatingStyles(floatingStyles),
    [floatingStyles]
  );

  useEffect(() => {
    if (!visible || !update) return undefined;
    update();
    const onResize = () => visible && update();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [visible, update, referenceElement, active?.node]);

  if (!editor || !visible || !active) return null;

  const { node, pos } = active;
  const hasAttribution = pathstoneQuoteHasAttribution(node);
  const currentVariant = node.attrs?.variant || 'black';

  const handleAddAttribution = (e) => {
    e.preventDefault();
    e.stopPropagation();
    editor.chain().focus().addPathstoneQuoteAttribution().run();
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    editor.chain().focus().setNodeSelection(pos).deleteSelection().run();
    setColorOpen(false);
  };

  const handleVariant = (variantId) => {
    editor
      .chain()
      .setNodeSelection(pos)
      .setPathstoneQuoteVariant(variantId)
      .focus()
      .run();
    setColorOpen(false);
  };

  const menuContent = (
    <div
      ref={(node) => {
        floatingRef.current = node;
        refs.setFloating(node);
      }}
      tabIndex={-1}
      data-floating-ui-focusable=""
      className={postEditorFloatingContainerClasses(POST_EDITOR_FLOATING_MENU_KINDS.blockquote, {
        popoverOpen: colorOpen,
      })}
      data-floating-kind={POST_EDITOR_FLOATING_MENU_KINDS.blockquote}
      style={postEditorFloatingPositionStyle(adjustedFloatingStyles)}
    >
      <div {...postEditorFloatingToolbarProps(POST_EDITOR_FLOATING_MENU_KINDS.blockquote, { ariaLabel: 'Pull quote' })}>
        <div
          role="group"
          className={`${postEditorFloatingToolbarGroupClasses(POST_EDITOR_FLOATING_MENU_KINDS.blockquote)} ps-toolbar-popover-anchor`}
          ref={colorAnchorRef}
        >
          <Button
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setColorOpen((open) => !open);
            }}
            tooltip="Color"
            data-style="ghost"
            data-weight="small"
            data-active-state={colorOpen ? 'on' : 'off'}
          >
            <PaintBucketIcon className="tiptap-button-icon" />
          </Button>

          {colorOpen && (
            <QuoteColorPickerMenu
              includeMenuChrome
              selectedVariant={currentVariant}
              onSelect={handleVariant}
            />
          )}

          {!hasAttribution && (
            <>
              <Separator orientation="vertical" />
              <Button
                onClick={handleAddAttribution}
                tooltip="Add attribution"
                data-style="ghost"
                data-weight="small"
              >
                <PlusIcon className="tiptap-button-icon" />
              </Button>
            </>
          )}

          <Separator orientation="vertical" />
          <Button onClick={handleDelete} tooltip="Delete" data-style="ghost" data-weight="small">
            <TrashIcon className="tiptap-button-icon" />
          </Button>
        </div>
      </div>
    </div>
  );

  const portalTarget = editor.view?.dom?.parentElement || document.body;
  return createPortal(menuContent, portalTarget);
}
